import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { useTranslation } from 'react-i18next';

interface GoogleMapPickerProps {
  apiKey: string;
  onLocationSelect: (location: MapLocation) => void;
  initialLocation?: Coordinates;
  initialAddress?: Address;
}

interface MapLocation {
  coordinates: Coordinates;
  address?: Address;
}

interface Coordinates {
  latitude: number;
  longitude: number;
}

interface Address {
  street: string;
  ward: string;
  city: string;
  state: string;
  country: string;
  postalCode?: string;
}

const GoogleMapPicker: React.FC<GoogleMapPickerProps> = ({
  apiKey,
  onLocationSelect,
  initialLocation,
  initialAddress
}) => {
  const { t } = useTranslation();
  const mapContainer = useRef<HTMLDivElement>(null);
  const searchBox = useRef<HTMLInputElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const marker = useRef<google.maps.Marker | null>(null);
  const autocomplete = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);

  const getWardFromGeocoding = async (lat: number, lng: number): Promise<string> => {
    try {
      const geocoder = new google.maps.Geocoder();
      const results = await geocoder.geocode({
        location: { lat, lng },
        language: 'vi' // Request Vietnamese results
      });
      
      if (results.results) {
        // Look through all results for ward information
        for (const result of results.results) {
          for (const component of result.address_components) {
            if (component.long_name.includes('Phường') || 
                component.long_name.includes('Xã') ||
                component.types.includes('sublocality_level_1')) {
              return component.long_name;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error getting ward:', error);
    }
    return '';
  };

  const handleLocationSelect = async (lat: number, lng: number) => {
    try {
      const geocoder = new google.maps.Geocoder();
      const result = await geocoder.geocode({ 
        location: { lat, lng },
        language: 'vi' // Request Vietnamese results
      });
      
      if (result.results[0]) {
        const address = parseGoogleAddress(result.results[0].address_components);
        
        // If ward is empty, try to get it from additional geocoding
        if (!address.ward) {
          address.ward = await getWardFromGeocoding(lat, lng);
        }

        onLocationSelect({
          coordinates: { latitude: lat, longitude: lng },
          address
        });
      }
    } catch (error) {
      console.error('Error geocoding location:', error);
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        if (map.current && marker.current) {
          const latLng = { lat: latitude, lng: longitude };
          map.current.setCenter(latLng);
          marker.current.setPosition(latLng);
          await handleLocationSelect(latitude, longitude);
        }
        setIsLocating(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        setIsLocating(false);
      },
      { enableHighAccuracy: true }
    );
  };

  useEffect(() => {
    const initializeMap = async () => {
      if (!mapContainer.current || !searchBox.current) return;

      try {
        setIsLoading(true);
        const loader = new Loader({
          apiKey,
          version: "weekly",
          libraries: ["places"]
        });

        const google = await loader.load();
        const defaultLocation = initialLocation || { latitude: 10.762622, longitude: 106.660172 }; // Ho Chi Minh City

        const mapInstance = new google.maps.Map(mapContainer.current, {
          center: { lat: defaultLocation.latitude, lng: defaultLocation.longitude },
          zoom: 15,
          mapTypeControl: true,
          streetViewControl: false
        });

        map.current = mapInstance;

        const markerInstance = new google.maps.Marker({
          map: mapInstance,
          position: { lat: defaultLocation.latitude, lng: defaultLocation.longitude },
          draggable: true
        });

        marker.current = markerInstance;

        // Initialize Places Autocomplete
        const autocompleteInstance = new google.maps.places.Autocomplete(searchBox.current, {
          fields: ['address_components', 'geometry', 'name'],
          componentRestrictions: { country: 'vn' }
        });

        autocomplete.current = autocompleteInstance;

        // Add place_changed event listener
        autocompleteInstance.addListener('place_changed', async () => {
          const place = autocompleteInstance.getPlace();
          if (place.geometry?.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            // Update map and marker
            mapInstance.setCenter({ lat, lng });
            markerInstance.setPosition({ lat, lng });

            // Get address details
            if (place.address_components) {
              const address = parseGoogleAddress(place.address_components);
              
              // If ward is empty, try to get it from additional geocoding
              if (!address.ward) {
                address.ward = await getWardFromGeocoding(lat, lng);
              }

              onLocationSelect({
                coordinates: { latitude: lat, longitude: lng },
                address
              });
            }
          }
        });

        // Add click event
        mapInstance.addListener('click', (e: google.maps.MapMouseEvent) => {
          const position = e.latLng;
          if (position && markerInstance) {
            markerInstance.setPosition(position);
            handleLocationSelect(position.lat(), position.lng());
          }
        });

        // Add drag event
        markerInstance.addListener('dragend', () => {
          const position = markerInstance.getPosition();
          if (position) {
            handleLocationSelect(position.lat(), position.lng());
          }
        });
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeMap();

    return () => {
      map.current = null;
      marker.current = null;
      autocomplete.current = null;
    };
  }, [apiKey, initialLocation]);

  const parseGoogleAddress = (components: google.maps.GeocoderAddressComponent[]): Address => {
    console.log('Raw address components:', components);

    let address: Address = {
      street: '',
      ward: '',
      city: '',
      state: '',
      country: 'Vietnam',
      postalCode: ''
    };

    let streetNumber = '';
    let route = '';

    components.forEach(component => {
      const types = component.types;
      console.log('Component:', component.long_name, 'Types:', types);

      // Handle ward (phường)
      if (types.includes('sublocality_level_1') || 
          types.includes('sublocality') || 
          component.long_name.includes('Phường') || 
          component.long_name.includes('Xã')) {
        address.ward = component.long_name;
      }
      // Handle street number
      else if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      // Handle street name
      else if (types.includes('route')) {
        route = component.long_name;
      }
      // Handle district/city
      else if (types.includes('administrative_area_level_2') || 
               types.includes('locality') ||
               component.long_name.startsWith('Quận') ||
               component.long_name.startsWith('Huyện')) {
        address.city = component.long_name;
      }
      // Handle state/province
      else if (types.includes('administrative_area_level_1')) {
        address.state = component.long_name;
      }
      // Handle country
      else if (types.includes('country')) {
        address.country = component.long_name;
      }
      // Handle postal code
      else if (types.includes('postal_code')) {
        address.postalCode = component.long_name;
      }
    });

    // Combine street number and route
    address.street = [streetNumber, route].filter(Boolean).join(' ');

    // If ward is still empty, try to find it in the sublocality
    if (!address.ward) {
      const sublocalityComponent = components.find(component => 
        component.types.includes('sublocality') || 
        component.types.includes('sublocality_level_1') ||
        component.types.includes('neighborhood')
      );
      if (sublocalityComponent) {
        address.ward = sublocalityComponent.long_name;
      }
    }

    console.log('Parsed address:', address);
    return address;
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            ref={searchBox}
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('locations.map.search')}
            disabled={isLoading}
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleUseMyLocation}
          disabled={isLocating}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm min-w-[160px] justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          {isLocating ? t('common.locating') : t('common.useMyLocation')}
        </button>
      </div>
      <div className="h-96">
        <div ref={mapContainer} className="h-full w-full rounded-lg" />
      </div>
    </div>
  );
};

export default GoogleMapPicker; 